import { Card, Stack, Typography } from '@mui/material';
import { SVLink } from '../core';
import { useState } from 'react';
import ResetPasswordModal from '../modals/ResetPasswordModal';
import ChangeEmailModal from '../modals/ChangeEmailModal';
import ChangePhoneModal from '../modals/ChangePhoneModal';
import { CustomerDetails } from '@/types/customers';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import {
  useResetCustomerPassword,
  useStartCustomerUpdate,
  useChangeCustomerEmail,
  useChangeCustomerPhone,
  useReactivateCustomer,
} from '@/hooks/requests/useCustomers';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import { Status } from '@/types/common';
import { ROUTES } from '@/constants/routes';
import ReactivateModal from '../modals/ReactivateModal';
import ResendActivationModal from './modals/ResendActivationModal';
import CustomerChangeDepositRulesModal from './modals/CustomerChangeDepositRulesModal';
import CustomerDeactivateModal from './modals/CustomerDeactivateModal';

type CustomerDetailsActionsProps = {
  customerDetails: CustomerDetails;
};

export default function CustomerDetailsActions({
  customerDetails,
}: CustomerDetailsActionsProps) {
  const { isStaff } = useAuthUser();
  const { uuid, status, email, company, advisors } = customerDetails;
  const { scrollToTop } = useScrollToTop();

  const [openResetPassword, setOpenResetPassword] = useState(false);
  const [openChangeEmail, setOpenChangeEmail] = useState(false);
  const [openChangePhone, setOpenChangePhone] = useState(false);
  const [openDeactivate, setOpenDeactivate] = useState(false);
  const [openReactivate, setOpenReactivate] = useState(false);
  const [openResend, setOpenResend] = useState(false);
  const [openDepositRules, setOpenDepositRules] = useState(false);

  const { resetCustomerPassword, isResetCustomerPasswordPending } =
    useResetCustomerPassword(uuid);
  const { startCustomerUpdate } = useStartCustomerUpdate(uuid);
  const { changeCustomerEmail, isChangeCustomerEmailPending } =
    useChangeCustomerEmail(uuid);
  const { changeCustomerPhone, isChangeCustomerPhonePending } =
    useChangeCustomerPhone(uuid);
  const { reactivateCustomer, isReactivateCustomerPending } =
    useReactivateCustomer(uuid);

  return (
    <Card variant="fullHeight">
      <Typography variant="h5" mb={2}>
        Actions
      </Typography>
      <Stack spacing={2}>
        <SVLink onClick={() => setOpenResetPassword(true)} to="">
          Reset Password
        </SVLink>
        <SVLink onClick={() => setOpenChangeEmail(true)} to="">
          Change Email
        </SVLink>
        <SVLink onClick={() => setOpenChangePhone(true)} to="">
          Change Auth Phone
        </SVLink>
        {status === Status.ACTIVE && (
          <SVLink
            to={`${ROUTES.BANK_ACCOUNT_APPLICATIONS}?customerId=${uuid}&companyId=${company.uuid}&advisorId=${advisors[0].uuid}`}>
            Start Bank Account Application
          </SVLink>
        )}
        <SVLink
          to={`${ROUTES.CUSTOMERS}/${uuid}${ROUTES.EXTERNAL_ACCOUNTS}/add`}>
          Link External Account
        </SVLink>
        {isStaff && (
          <>
            <SVLink onClick={() => setOpenDepositRules(true)} to="">
              Change Deposit Rules
            </SVLink>
            {status === Status.ACTIVE ? (
              <SVLink
                onClick={() => setOpenDeactivate(true)}
                to=""
                isWarningLink>
                Deactivate
              </SVLink>
            ) : (
              <SVLink
                onClick={() => setOpenReactivate(true)}
                to=""
                isWarningLink>
                Reactivate
              </SVLink>
            )}
            <SVLink onClick={() => setOpenResend(true)} isWarningLink to="">
              Resend Activation Email
            </SVLink>
          </>
        )}
      </Stack>
      <CustomerDeactivateModal
        id={uuid}
        open={openDeactivate}
        setOpen={setOpenDeactivate}
      />
      <ReactivateModal
        userType="Customer"
        open={openReactivate}
        setOpen={setOpenReactivate}
        reactivate={reactivateCustomer}
        isReactivatePending={isReactivateCustomerPending}
        scrollToTop={scrollToTop}
      />
      <ResetPasswordModal
        userType="Customer"
        open={openResetPassword}
        setOpen={setOpenResetPassword}
        resetPassword={resetCustomerPassword}
        isResetPasswordLoading={isResetCustomerPasswordPending}
        scrollToTop={scrollToTop}
      />
      <ChangeEmailModal
        userType="Customer"
        open={openChangeEmail}
        setOpen={setOpenChangeEmail}
        sendOtp={startCustomerUpdate}
        changeEmail={changeCustomerEmail}
        isChangeEmailLoading={isChangeCustomerEmailPending}
        scrollToTop={scrollToTop}
      />
      <ChangePhoneModal
        userType="Customer"
        open={openChangePhone}
        setOpen={setOpenChangePhone}
        sendOtp={startCustomerUpdate}
        changePhone={changeCustomerPhone}
        isChangePhonePending={isChangeCustomerPhonePending}
        scrollToTop={scrollToTop}
      />
      <ResendActivationModal
        open={openResend}
        setOpen={setOpenResend}
        email={email}
      />
      <CustomerChangeDepositRulesModal
        open={openDepositRules}
        setOpen={setOpenDepositRules}
        customerDetails={customerDetails}
      />
    </Card>
  );
}
